import { LitElement, html, css } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { centsToDollars, resizeImageSrc, unescapeStr } from '../../lib/utils.js'

export class CartDrawerInterstitial extends LitElement {
  static styles = [
    css`
      .interstitial {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        gap: 20px;
      }
      .interstitial-container {
        display: flex;
        flex-direction: column;
        padding: 0px 16px 24px;
        overflow-y: scroll;
        gap: 24px;
      }
      .interstitial-header-container {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-right: 32px;
      }
      .interstitial-header {
        font-weight: 400;
        font-size: 18px;
      }
      .interstitial-sub-header {
        font-size: 14px;
        font-weight: 300;
      }
      @media (min-width: 768px) {
        .interstitial-sub-header {
          font-size: 16px;
          font-weight: 300;
          line-height: 132%;
        }
      }

      .interstitial-image-container > img {
        width: 100%;
        height: 100%;
      }

      .metafield-rich_text_field {
        font-size: 16px;
        font-weight: 400;
      }

      .metafield-rich_text_field p {
        margin: 0 0 20px 0;
      }

      .metafield-rich_text_field ul,
      .interstitial-bullet-points,
      ul {
        padding: 0 0 0 20px;
        font-size: 14px;
        font-weight: 400;
        margin: 0;
        gap: 20px;
        display: flex;
        flex-direction: column;
      }

      @media (min-width: 768px) {
        .interstitial-bullet-points {
          font-size: 16px;
          line-height: 132%;
        }

        .metafield-rich_text_field ul,
        .interstitial-bullet-points,
        ul {
          font-size: 16px;
        }
      }

      .interstitial-paragraph-container {
        font-size: 14px;
        font-weight: 400;
      }
      @media (min-width: 768px) {
        .interstitial-paragraph-container {
          font-size: 16px;
          line-height: 132%;
        }
      }

      .interstitial-btn-container {
        border-top: 1px solid #dcdcda;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-top: auto;
      }

      .banner {
        padding: 12px;
        background: #08906e;
        text-align: center;
        font-size: 14px;
        font-weight: 400;
      }

      .flex-center {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .gap-2 {
        gap: 0.5rem;
      }

      .btn-primary {
        width: 100%;
        background-color: #0a0a0a;
        color: #fff;
        font-size: 14px;
        font-weight: 400;
        font-family: 'ABC Oracle', 'Arial', 'Helvetica', 'sans-serif';
        line-height: 20px;
        letter-spacing: 1px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        border: none;
        padding: 20px 48px 20px 48px;
        border-radius: 100px;
        text-transform: uppercase;
        transition-duration: 0.2s;
        transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      }

      .btn-primary:hover {
        background: #08906e;
        color: #0a0a0a;
      }

      .btn-primary s {
        color: #8c8c8b;
      }

      .btn-primary:hover s {
        color: #0a0a0a;
      }

      .btn-secondary {
        cursor: pointer;
        border-width: 2px;
        border-radius: 100px;
        border-style: solid;
        border-color: rgb(10 10 10);
        background-color: rgb(255 255 255);
        padding-top: 1.25rem;
        padding-bottom: 1.25rem;
        padding-left: 3rem;
        padding-right: 3rem;
        font-size: 14px;
        font-weight: 400;
        font-family: 'ABC Oracle', 'Arial', 'Helvetica', 'sans-serif';
        line-height: 20px;
        letter-spacing: 1px;
        text-transform: uppercase;
        color: rgb(10 10 10);
        transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 200ms;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
      }

      .btn-secondary:hover {
        background-color: rgb(244 244 242);
        color: rgb(10 10 10);
      }
      @media (min-width: 768px) {
        .btn-primary,
        .btn-secondary {
          font-size: 16px;
        }
      }

      .interstitial-compare-price {
        margin-left: 5px;
      }
    `,
  ]

  static properties = {
    header: { type: String },
    subHeader: { type: String },
    variant: { type: Object },
    image: { type: Object },
    bulletPoints: { type: Array },
    alwaysShow: { type: Boolean },
    enableSwap: { type: Boolean },
  }

  connectedCallback() {
    super.connectedCallback()

    this.parent_title = 'Product'
    this.ctaText = 'Add'

    window.MicroBus.on(`cart:open-interstitial`, ({ detail }) => {
      const {
        header,
        sub_header,
        variant,
        is_selling_plan,
        image,
        bullet_points,
        paragraph,
        always_show,
        parent_variant_id,
        enable_swap,
        parent_key,
        parent_title,
        parent_price,
        use_incremental_price,
      } = detail

      this.header = header
      this.subHeader = sub_header
      this.variant = variant
      this.isSellingPlan = is_selling_plan
      this.image = image ?? variant?.featured_image
      this.bulletPoints = bullet_points
      this.paragraph = paragraph
      this.alwaysShow = always_show
      this.enableSwap = enable_swap
      this.price = variant?.price
      this.parent_variant_id = parent_variant_id
      this.parent_key = parent_key
      this.parent_title = parent_title ?? 'Product'
      this.parent_price = parent_price
      this.compare_at_price = this.variant?.compare_at_price
      this.ctaText = 'Add To Cart'
      this.use_incremental_price = use_incremental_price

      if (this.isSellingPlan) {
        this.selling_plan_id = this.variant?.selling_plan_allocations[0]?.selling_plan_id
        this.price = this.variant?.selling_plan_allocations[0]?.price
      }

      if (use_incremental_price && this.parent_price) {
        this.price = this.price - this.parent_price
      }

      const data = {
        overlayStyle: document.querySelector('#side-cart').hasAttribute('open') ? 'transparent' : '',
      }
      window.MicroBus.emit('side-cart-interstitial:toggle', data)
      window.MicroBus.emit('igEvents:push', {
        event: `Interstitial - Opened`,
        properties: {
          parent_variant: this.parent_title,
        },
      })
      if (window?.gtag) {
        gtag('event', `Interstitial - Opened`, {
          event_detail: `${this.header}`,
          event_detail2: `Parent Variant - ${this.parent_variant_id}`,
        })
      }
    })
  }
  disconnectedCallback() {
    super.connectedCallback()
    window.MicroBus.off(`${this.id}:toggle`)
  }

  render() {
    return html` <div class="interstitial">
      <div class="banner">
        <div class="flex-center gap-2">
          <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12.3125 3.93763L6.1875 10.0624L3.125 7.00013"
              stroke="#0A0A0A"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>${this.parent_title?.replace(/\b[0-9]\s+Month\s+Supply\b/, '')} added to cart</span>
        </div>
      </div>
      <div class="interstitial-container">
        <div class="interstitial-header-container">
          <div class="interstitial-header">${this.header}</div>
          ${this.subHeader ? html`<div class="interstitial-sub-header">${this.subHeader}</div>` : ''}
        </div>
        <div class="interstitial-image-container">${this._renderImage()}</div>
        ${this._renderBulletPoints()} ${this._renderParagraph()}
      </div>
      <div class="interstitial-btn-container">
        <add-button
          id="AddToCart"
          data-variant-id="${this.variant?.id}"
          data-selling-plan-id="${this.selling_plan_id}"
          data-variant-name="${this.variant?.name}"
          disable-cart-toggle
          type="interstitial"
          data-parent-variant-id="${this.parent_variant_id}"
          data-parent-key="${this.parent_key}"
          ?swap="${this.enableSwap}"
          @click=${this._closeInterstitial}
        >
          <button class="btn-primary">${this.ctaText} – $${centsToDollars(this.price)} ${this._renderComparePrice()}</button>
        </add-button>
        <button class="btn-secondary" @click=${this._continueToCart}>No Thanks - View Cart</button>
      </div>
    </div>`
  }

  _renderBulletPoints() {
    if (!this.bulletPoints || this.bulletPoints.length < 1) return

    return html`<div class="interstitial-bullet-points-container">
      <ul class="interstitial-bullet-points">
        ${this.bulletPoints.map((bulletPoint) => html`<li class="interstitial-bullet-point">${bulletPoint}</li>`)}
      </ul>
    </div>`
  }

  _renderParagraph() {
    if (!this.paragraph) return
    return html`<div class="interstitial-paragraph-container">${unsafeHTML(unescapeStr(this.paragraph))}</div>`
  }

  _renderImage() {
    if (!this.image) return
    return html`<img
      src="${this.image.src ? this.image.src : resizeImageSrc(this.image, 625)}"
      alt="${this.image?.alt}"
      height="${this.image?.height}"
      width="${this.image?.width}"
    />`
  }

  _renderComparePrice() {
    if (this.compare_at_price && this.compare_at_price > 0 && this.use_incremental_price !== true) {
      return html`<s class="interstitial-compare-price"> $${centsToDollars(this.compare_at_price)}</s>`
    }
  }

  _continueToCart() {
    window.MicroBus.emit('side-cart-interstitial:toggle')
    if (window?.gtag) {
      gtag('event', `Interstitial - Continue to Cart`, {
        event_detail: `${this.header}`,
      })

      gtag('event', `Interstitial - Closed`, {
        event_detail: `${this.header}`,
      })
    }
  }

  _closeInterstitial() {
    window.MicroBus.emit('side-cart-interstitial:toggle')

    if (window?.gtag) {
      gtag('event', `Interstitial - Closed`, {
        event_detail: `${this.header}`,
      })
    }
  }
}

customElements.define('cart-drawer-interstitial', CartDrawerInterstitial)
